<template>

    <div :class="elClass" :style="{width: (width+2+'px'), margin: 'auto'}">

        <div v-for="pageNum in pageNums"
             v-show="!isLoading"
             :id="id && `${id}-${pageNum}`"
             :key="pageNum"
             :class="`pdf-page pdf-p-${pageNum}`"
        >
            <canvas :class="page ? '' : 'mb-3'"/>

            <div v-if="!disableTextLayer" class="textLayer"/>

            <div v-if="!disableAnnotationLayer" class="annotationLayer"/>
        </div>


        <div v-if="isLoading" class="card">
            <slot name="skeletonLoading">
                <div>
                    <div class="flex mb-3">
                        <Skeleton class="mr-2" shape="circle" size="4rem"></Skeleton>
                        <div>
                            <Skeleton class="mb-2" width="10rem"></Skeleton>
                            <Skeleton class="mb-2" width="5rem"></Skeleton>
                            <Skeleton height=".5rem"></Skeleton>
                        </div>
                    </div>
                    <Skeleton class="mb-3" height="150px" width="100%"></Skeleton>
                    <Skeleton class="mb-3" height="150px" width="100%"></Skeleton>
                    <Skeleton class="mb-3" height="150px" width="100%"></Skeleton>
                    <Skeleton class="mb-3" height="150px" width="100%"></Skeleton>
                    <Skeleton height="150px" width="100%"></Skeleton>
                    <div class="flex justify-content-between mt-3">
                        <Skeleton height="2rem" width="4rem"></Skeleton>
                        <Skeleton height="2rem" width="4rem"></Skeleton>
                    </div>
                </div>
            </slot>
        </div>

        <slot></slot>

    </div>


</template>

<script>

import * as pdfjsLib from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/webpack'
import {Xapi} from "@xnpmpackages/xcomponents/dist/index";
//import store from "../../../store";


pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

export default {

    name: "PdfView",

    emits: ['renderedSuccess', 'loadingFailed', 'update:loading', 'loadProgress', 'renderProgress', 'renderedFirstPage', 'pageCountUpdated'],

    props: {

        disableAnnotationLayer: {
            type: Boolean,
            default: true,
        },
        disableTextLayer: {
            type: Boolean,
            default: true,
        },
        height: [Number, String],
        id: String,
        page: Number,
        source: {
            type: [Object, String, Uint8Array, null],
            required: true,
        },
        width: [Number, String],
        loading: {
            default: false,
            type: Boolean
        },
        showAfterFirstPageRendered: {
            default: true,
            type: Boolean
        },
        skeletonScale: {
            type: [Number],
            default: 1,
        }

    },

    data() {
        return {

            document: null,
            pageCount: null,
            pageNums: [],
            isLoading: this.loading,
            renderedPages: 0,
            loadProgress: 0,
            renderProgress: 0,

        }
    },

    watch: {

        disableAnnotationLayer() {
            this.render()
        },
        disableTextLayer() {
            this.render()
        },
        height() {
            this.render()
        },
        page() {
            this.render()
        },
        source: {
            immediate: true,
            async handler() {
                await this.reload();
            },
        },
        width() {
            this.render()
        },
        pageCount: {
            immediate: true,
            async handler() {
                this.$emit('pageCountUpdated', this.pageCount);
            }
        }
    },

    methods: {

        pageRendered() {

            if (this.renderedPages === 0) {

                if (this.showAfterFirstPageRendered) {
                    this.isLoading = false;
                    this.$emit('update:loading', false);
                }

                this.$emit('renderedFirstPage');

            }

            this.renderedPages++;
            this.renderProgress = Math.round((this.renderedPages / this.pageNums.length * 100));

            this.$emit('renderProgress', this.renderProgress);

        },

        async reload() {
            this.isLoading = true;
            this.$emit('update:loading', true);
            await this.load();
            await this.render();
        },
        getPageDimensions(ratio) {
            let width, height

            if (this.height && !this.width) {
                height = this.height
                width = height / ratio
            } else {
                width = this.width || this.$el.clientWidth
                height = width * ratio
            }

            return [width, height]
        },
        /**
         * Loads a PDF document. Defines a password callback for protected
         * documents.
         *
         * NOTE: Ignored if source property is not provided.
         */
        async load() {


            if (!this.source) {
                return
            }


            try {

                let url

                if (!this.source.startsWith('blob:')) {
                    url = URL.createObjectURL(await Xapi.getFileAsFile(this.source));
                } else {
                    url = this.source
                }

                const documentLoadingTask = pdfjsLib.getDocument({
                    url: url,
                    /*httpHeaders: {
                        'Session_id': store.state.session_id
                    },*/
                    withCredentials: true,

                });

                documentLoadingTask.onPassword = (callback, reason) => {
                    const retry = reason === pdfjsLib.PasswordResponses.INCORRECT_PASSWORD
                    this.$emit('password-requested', callback, retry)
                }

                documentLoadingTask.onProgress = progress => {
                    this.loadProgress = Math.round((progress.loaded / progress.total) * 100);
                    this.$emit('loadProgress', this.loadProgress);
                };

                this.document = await documentLoadingTask.promise

                this.pageCount = this.document.numPages


            } catch (e) {
                this.document = null
                this.pageCount = null
                this.pageNums = []
                this.$emit('loadingFailed', e)
            }
        },
        /**
         * Renders the PDF document as SVG element(s) and additional layers.
         *
         * NOTE: Ignored if the document is not loaded.
         */
        async render() {
            if (!this.document) {
                return
            }

            this.renderProgress = 0;
            this.renderedPages = 0;

            try {
                this.pageNums = this.page
                    ? [this.page]
                    : [...Array(this.document.numPages + 1).keys()].slice(1)

                await Promise.all(
                    this.pageNums.map(async (pageNum, i) => {
                        const page = await this.document.getPage(pageNum)
                        const [canvas, div1, div2] = this.$el.children[i].children
                        const [actualWidth, actualHeight] = this.getPageDimensions(
                            page.view[3] / page.view[2]
                        )

                        canvas.style.width = `${Math.floor(actualWidth)}px`
                        canvas.style.height = `${Math.floor(actualHeight)}px`

                        await this.renderPage(page, canvas, actualWidth)
                        this.pageRendered();
                        /*let ctx = canvas.getContext('2d')
                        ctx.font = "48px serif";
                        ctx.fillText("Hello world", 10, 50);*/


                        if (!this.disableTextLayer) {
                            await this.renderPageTextLayer(page, div1, actualWidth)
                        }

                        if (!this.disableAnnotationLayer) {
                            await this.renderPageAnnotationLayer(
                                page,
                                div2 || div1,
                                actualWidth
                            )
                        }

                    })
                )

                this.isLoading = false;
                this.$emit('renderedSuccess');
                this.$emit('update:loading', false);
            } catch (e) {
                this.document = null
                this.pageCount = null
                this.pageNums = []
                this.$emit('loadingFailed', e)
            } finally {
                this.isLoading = false;
                this.$emit('update:loading', false);
            }
        },
        /**
         * Renders the page content.
         * @param {PDFPageProxy} page - Page proxy.
         * @param {HTMLCanvasElement} canvas - HTML canvas.
         * @param {number} width - Actual page width.
         */
        async renderPage(page, canvas, width) {
            const viewport = page.getViewport({
                scale: Math.ceil(width / page.view[2]) + 1,
            })

            canvas.width = viewport.width
            canvas.height = viewport.height

            await page.render({
                canvasContext: canvas.getContext('2d'),
                viewport,
                enableWebGL: true,
            }).promise
        },
        /**
         * Renders the annotation layer for the specified page.
         * @param {PDFPageProxy} page - Page proxy.
         * @param {HTMLElement} container - HTML container.
         * @param {number} width - Actual page width.
         */
        async renderPageAnnotationLayer(page, container, width) {
            pdfjsLib.AnnotationLayer.render({
                annotations: await page.getAnnotations(),
                div: container,
                linkService: this.linkService,
                page,
                renderInteractiveForms: false,
                viewport: page
                    .getViewport({
                        scale: width / page.view[2],
                    })
                    .clone({
                        dontFlip: true,
                    }),
            })
        },
        /**
         * Renders the text layer for the specified page.
         * @param {PDFPageProxy} page - Page proxy.
         * @param {HTMLElement} container - HTML container.
         * @param {number} width - Actual page width.
         */
        async renderPageTextLayer(page, container, width) {
            await pdfjsLib.renderTextLayer({
                container,
                textContent: await page.getTextContent(),
                viewport: page.getViewport({
                    scale: width / page.view[2],
                }),
            }).promise
        },

    },
    computed: {

        elClass() {

            let def = ['vue-pdf-embed'];


            if ((!this.$appState.darkTheme && this.$appState.theme !== 'vela-blue') && !this.isLoading) {
                def.push('border-1')
                def.push('border-bluegray-100')
            }
            return def;

        }

    }

}
</script>

<style scoped>

</style>